import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Establishment Context
 * Provides selected establishment ID throughout the app
 */
const EstablishmentContext = createContext({
    selectedEstablishmentId: null,
    setSelectedEstablishmentId: () => {},
    establishments: [],
    canSelect: false,
    currentEstablishment: null,
    currentEstablishmentName: null,
    loading: true
});

export const useEstablishment = () => useContext(EstablishmentContext);

export const EstablishmentProvider = ({ children }) => {
    const { api, user, isAuthenticated } = useAuth();
    const [selectedEstablishmentId, setSelectedEstablishmentId] = useState(null);
    const [establishments, setEstablishments] = useState([]);
    const [canSelect, setCanSelect] = useState(false);
    const [currentEstablishment, setCurrentEstablishment] = useState(null);
    const [currentEstablishmentName, setCurrentEstablishmentName] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const fetchEstablishments = useCallback(async () => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        
        try {
            const response = await api().get('/establishments/selector');
            const estList = response.data.establishments || [];
            setEstablishments(estList);
            setCanSelect(response.data.can_select || false);
            setCurrentEstablishmentName(response.data.current_establishment_name);
            
            // Find the current establishment with full data
            const currentEst = estList.find(e => e.id === response.data.current_establishment_id);
            setCurrentEstablishment(currentEst || null);
            
            // Set default selected establishment
            if (response.data.current_establishment_id) {
                setSelectedEstablishmentId(response.data.current_establishment_id);
            } else if (estList.length > 0 && response.data.can_select) {
                // Admin/Directeur without assigned establishment - show all
                setSelectedEstablishmentId(null);
            }
        } catch (error) {
            console.error('Error fetching establishments:', error);
        } finally {
            setLoading(false);
        }
    }, [api, isAuthenticated]);
    
    useEffect(() => {
        fetchEstablishments();
    }, [fetchEstablishments, user]);
    
    // Get selected establishment object
    const selectedEstablishment = selectedEstablishmentId 
        ? establishments.find(e => e.id === selectedEstablishmentId) 
        : currentEstablishment;
    
    return (
        <EstablishmentContext.Provider value={{
            selectedEstablishmentId,
            setSelectedEstablishmentId,
            establishments,
            canSelect,
            currentEstablishment,
            currentEstablishmentName,
            selectedEstablishment,
            loading
        }}>
            {children}
        </EstablishmentContext.Provider>
    );
};

/**
 * Establishment Selector Component
 * Shows dropdown for ADMIN/DIRECTEUR to switch between establishments
 */
const EstablishmentSelector = ({ className = '' }) => {
    const { 
        selectedEstablishmentId, 
        setSelectedEstablishmentId, 
        establishments, 
        canSelect,
        selectedEstablishment,
        currentEstablishmentName,
        loading 
    } = useEstablishment();
    
    // Get color based on establishment
    const getEstablishmentColor = (est) => {
        if (!est) return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
        const theme = est.theme || est.color;
        if (theme === 'red' || est.color === '#DC2626') {
            return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-400', color: '#DC2626' };
        }
        if (theme === 'blue' || est.color === '#2563EB') {
            return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-400', color: '#2563EB' };
        }
        return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-400', color: '#16A34A' };
    };
    
    // Don't show if user can't select or only has one establishment
    if (loading || !canSelect || establishments.length <= 1) {
        // Show current establishment name if assigned
        if (currentEstablishmentName && selectedEstablishment) {
            const colors = getEstablishmentColor(selectedEstablishment);
            return (
                <div className={`flex items-center gap-2 px-3 py-1.5 ${colors.bg} rounded-lg text-sm border ${colors.border} ${className}`}>
                    <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: colors.color || selectedEstablishment?.color }}
                    />
                    <span className={`font-bold ${colors.text}`}>
                        {selectedEstablishment?.name || currentEstablishmentName}
                    </span>
                </div>
            );
        }
        return null;
    }
    
    const colors = getEstablishmentColor(selectedEstablishment);
    
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            {selectedEstablishment && (
                <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: selectedEstablishment?.color || '#6B7280' }}
                />
            )}
            <Select 
                value={selectedEstablishmentId || 'all'} 
                onValueChange={(val) => setSelectedEstablishmentId(val === 'all' ? null : val)}
            >
                <SelectTrigger className={`w-48 h-8 text-sm ${colors.bg} border ${colors.border}`}>
                    <SelectValue placeholder="Tous les établissements" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">
                        <span className="flex items-center gap-2">
                            <Building2 className="w-3 h-3" />
                            Tous les établissements
                        </span>
                    </SelectItem>
                    {establishments.map(est => {
                        const estColors = getEstablishmentColor(est);
                        return (
                            <SelectItem key={est.id} value={est.id}>
                                <span className="flex items-center gap-2">
                                    <div 
                                        className="w-3 h-3 rounded-full" 
                                        style={{ backgroundColor: est.color || '#6B7280' }}
                                    />
                                    <span className={`font-semibold`}>{est.name}</span>
                                </span>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
        </div>
    );
};

export default EstablishmentSelector;
